import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { DollarSign, Link2, Loader2, Stethoscope, Video, CheckCircle2, Clock, Landmark, ShieldCheck, AlertTriangle, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = process.env.REACT_APP_BACKEND_URL;
const OP_FEE = 10.5;
const brl = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export const SoloCobranca = () => {
  const token = localStorage.getItem("veloclin_token");
  const [open, setOpen] = useState(false);
  const [patient, setPatient] = useState("");
  const [type, setType] = useState("presencial");
  const [amount, setAmount] = useState(200);
  const [loading, setLoading] = useState(false);
  const [charges, setCharges] = useState([]);
  const [payout, setPayout] = useState(null);
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pin, setPin] = useState("");
  const [savingPayout, setSavingPayout] = useState(false);
  const emptyPayout = { bank_name: "", agency: "", account: "", account_type: "corrente", holder_name: "", document: "", pix_key_type: "cpf", pix_key: "" };
  const [pForm, setPForm] = useState(emptyPayout);

  const load = useCallback(async () => {
    if (!token) return;
    const r = await fetch(`${API}/api/solo/charges`, { headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) {
      const d = await r.json();
      setCharges(d.charges || []);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const pending = charges.filter((c) => c.payment_status !== "paid");
    if (!pending.length) return;
    const iv = setInterval(async () => {
      await Promise.all(pending.map((c) => fetch(`${API}/api/solo/charge/status/${c.session_id}`)));
      load();
    }, 3000);
    return () => clearInterval(iv);
  }, [charges, load]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/solo/payout`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.payout) { setPayout(d.payout); setPForm(d.payout); } })
      .catch(() => {});
  }, [token]);

  const openPayout = () => { setPForm(payout || emptyPayout); setPayoutOpen(true); };

  const requestSave = () => {
    const req = ["bank_name", "agency", "account", "holder_name", "document", "pix_key"];
    if (req.some((k) => !String(pForm[k] || "").trim())) return toast.error("Preencha todos os campos obrigatórios.");
    setPayoutOpen(false);
    setPin("");
    setPinOpen(true);
  };

  const confirmPin = async () => {
    if (pin.length !== 4) return toast.error("Digite o PIN de 4 dígitos.");
    setSavingPayout(true);
    try {
      const r = await fetch(`${API}/api/solo/payout`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(pForm),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Erro ao salvar");
      setPayout(d.payout);
      setPinOpen(false);
      toast.success("Conta de recebimento atualizada", { description: "Seus repasses serão creditados nesses dados." });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSavingPayout(false);
    }
  };

  const fee = amount > 0 ? Math.min(OP_FEE, amount) : 0;
  const net = +(amount - fee).toFixed(2);

  const generate = async () => {
    if (!payout) { toast.error("Configure sua conta de recebimento antes de cobrar."); setPayoutOpen(true); return; }
    if (!patient.trim()) return toast.error("Informe o nome do paciente.");
    if (!amount || amount <= 0) return toast.error("Informe um valor válido.");
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/solo/charge`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ patient_name: patient, consult_type: type, amount: Number(amount), origin_url: window.location.origin }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Erro ao gerar cobrança");
      window.open(d.checkout_url, "_blank");
      toast.success("Link de pagamento gerado (Pix / Cartão)", { description: "Checkout aberto em nova aba." });
      setOpen(false);
      setPatient("");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="font-heading text-base font-bold text-slate-900">Motor de Cobrança & Split</h3>
          <p className="text-xs text-slate-500">
            Gere cobranças por consulta. <span data-testid="op-fee-notice">Será cobrado uma taxa de <b className="text-slate-700">R$10,50</b> devido a custos operacionais de emissão de cobranças somente após o pagamento.</span>
          </p>
        </div>
        <button
          data-testid="new-charge-btn"
          onClick={() => setOpen(true)}
          className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-[0_2px_8px_rgba(37,99,235,0.3)] transition-colors hover:bg-blue-700"
        >
          <DollarSign className="h-4 w-4" />
          Gerar Cobrança
        </button>
      </div>

      {/* Conta de recebimento */}
      <div data-testid="payout-card" className="rounded-2xl border border-slate-200/70 bg-white p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Landmark className="h-4 w-4 text-blue-600" />
            <h4 className="font-heading text-sm font-bold text-slate-900">Conta de Recebimento (Repasses)</h4>
          </div>
          <button data-testid="edit-payout-btn" onClick={openPayout} className="flex shrink-0 items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50">
            <Pencil className="h-3.5 w-3.5" />{payout ? "Editar" : "Configurar"}
          </button>
        </div>
        {payout ? (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Conta bancária</p>
              <p className="mt-0.5 text-sm font-semibold text-slate-800">{payout.bank_name}</p>
              <p className="text-xs text-slate-500">Ag. {payout.agency} · Conta {payout.account} · {payout.account_type === "poupanca" ? "Poupança" : "Corrente"}</p>
              <p className="text-xs text-slate-500">{payout.holder_name} · {payout.document}</p>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-3">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-600">Chave Pix</p>
              <p data-testid="payout-pix" className="mt-0.5 text-sm font-semibold text-emerald-800">{payout.pix_key}</p>
              <p className="text-xs text-emerald-700">Tipo: {payout.pix_key_type}</p>
            </div>
          </div>
        ) : (
          <div data-testid="payout-empty" className="mt-3 flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
            <p className="flex-1 text-sm text-amber-800">Informe sua conta bancária e chave Pix para receber os repasses das cobranças.</p>
          </div>
        )}
      </div>

      {/* Extrato de liquidação */}
      <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
        <div className="border-b border-slate-100 px-5 py-3.5">
          <h4 className="font-heading text-sm font-bold text-slate-900">Extrato de Liquidação</h4>
          <p className="text-xs text-slate-500">{payout ? `Creditado em ${payout.bank_name} · Pix: ${payout.pix_key}` : "Configure sua conta de recebimento para receber os repasses"}</p>
        </div>
        <div className="hidden grid-cols-[1.2fr_110px_100px_120px_130px] gap-3 border-b border-slate-100 bg-slate-50/60 px-5 py-3 lg:grid">
          {["Transação / Paciente", "Tipo", "Bruto", "Taxa operacional", "Repasse"].map((h) => (
            <span key={h} className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{h}</span>
          ))}
        </div>
        {charges.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-400">Nenhuma cobrança gerada ainda.</div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="settlement-table">
            {charges.map((c) => {
              const tele = c.consult_type === "telemedicina";
              const paid = c.payment_status === "paid";
              return (
                <div key={c.session_id} data-testid={`charge-row-${c.id}`} className="grid grid-cols-1 gap-2 px-5 py-4 lg:grid-cols-[1.2fr_110px_100px_120px_130px] lg:items-center">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-900">{c.patient_name}</p>
                    <p className="font-mono-num truncate text-[11px] text-slate-400">#{c.id}</p>
                  </div>
                  <div>
                    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${tele ? "bg-violet-50 text-violet-700" : "bg-sky-50 text-sky-700"}`}>
                      {tele ? <Video className="h-3 w-3" /> : <Stethoscope className="h-3 w-3" />}
                      {tele ? "Telemedicina" : "Presencial"}
                    </span>
                  </div>
                  <span className="font-mono-num text-sm text-slate-700">{brl(c.gross)}</span>
                  <div>
                    <span className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-bold text-slate-600">
                      {brl(c.fee_amount)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-mono-num text-sm font-bold text-emerald-600">{brl(c.net)}</span>
                    {paid ? (
                      <span data-testid={`charge-status-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">
                        <CheckCircle2 className="h-3 w-3" />Creditado
                      </span>
                    ) : (
                      <span data-testid={`charge-status-${c.id}`} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-500">
                        <Clock className="h-3 w-3" />Aguardando
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal gerar cobrança */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="charge-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-slate-900">Gerar Cobrança</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Pix ou cartão. Taxa operacional de R$10,50 descontada somente após o pagamento.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs font-semibold text-slate-600">Nome do Paciente</Label>
              <Input data-testid="charge-patient-input" value={patient} onChange={(e) => setPatient(e.target.value)} className="mt-1.5" placeholder="Ex: João da Silva" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Tipo de Atendimento</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button data-testid="charge-type-presencial" onClick={() => setType("presencial")} className={`flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${type === "presencial" ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  <Stethoscope className="h-4 w-4" />Presencial
                </button>
                <button data-testid="charge-type-telemedicina" onClick={() => setType("telemedicina")} className={`flex items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors ${type === "telemedicina" ? "border-violet-600 bg-violet-50 text-violet-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                  <Video className="h-4 w-4" />Telemedicina
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Valor da Consulta (R$)</Label>
              <div className="relative mt-1.5">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">R$</span>
                <Input data-testid="charge-amount-input" type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} className="pl-9" />
              </div>
            </div>

            {/* Preview do split */}
            <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 text-sm">
              <div className="flex items-center justify-between text-slate-600"><span>Valor bruto</span><span className="font-mono-num font-semibold text-slate-900">{brl(amount)}</span></div>
              <div className="mt-1 flex items-center justify-between text-slate-600">
                <span>Taxa operacional (após o pagamento)</span>
                <span data-testid="charge-fee-preview" className="font-mono-num font-semibold text-rose-600">− {brl(fee)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between border-t border-slate-200 pt-2">
                <span className="font-semibold text-slate-700">Você recebe</span>
                <span data-testid="charge-net-preview" className="font-mono-num text-base font-bold text-emerald-600">{brl(net)}</span>
              </div>
            </div>

            <button
              data-testid="charge-generate-btn"
              onClick={generate}
              disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
              Gerar Link de Pagamento (Pix / Cartão)
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal conta de recebimento */}
      <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
        <DialogContent data-testid="payout-modal" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg font-bold text-slate-900">Conta de Recebimento</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Onde você recebe os repasses. Alterar exige confirmação por PIN.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto py-2 pr-1">
            <PField label="Banco" testid="payout-bank-input" value={pForm.bank_name} onChange={(v) => setPForm({ ...pForm, bank_name: v })} placeholder="Ex: Nubank / Itaú" />
            <div className="grid grid-cols-2 gap-3">
              <PField label="Agência" testid="payout-agency-input" value={pForm.agency} onChange={(v) => setPForm({ ...pForm, agency: v })} />
              <PField label="Conta" testid="payout-account-input" value={pForm.account} onChange={(v) => setPForm({ ...pForm, account: v })} />
            </div>
            <div>
              <Label className="text-xs font-semibold text-slate-600">Tipo de conta</Label>
              <div className="mt-1.5 grid grid-cols-2 gap-2">
                <button data-testid="payout-type-corrente" onClick={() => setPForm({ ...pForm, account_type: "corrente" })} className={ptoggle(pForm.account_type === "corrente")}>Corrente</button>
                <button data-testid="payout-type-poupanca" onClick={() => setPForm({ ...pForm, account_type: "poupanca" })} className={ptoggle(pForm.account_type === "poupanca")}>Poupança</button>
              </div>
            </div>
            <PField label="Titular da conta" testid="payout-holder-input" value={pForm.holder_name} onChange={(v) => setPForm({ ...pForm, holder_name: v })} />
            <PField label="CPF/CNPJ do titular" testid="payout-doc-input" value={pForm.document} onChange={(v) => setPForm({ ...pForm, document: v })} />
            <div>
              <Label className="text-xs font-semibold text-slate-600">Tipo de chave Pix</Label>
              <select data-testid="payout-pixtype-select" value={pForm.pix_key_type} onChange={(e) => setPForm({ ...pForm, pix_key_type: e.target.value })} className="mt-1.5 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600">
                <option value="cpf">CPF/CNPJ</option>
                <option value="email">E-mail</option>
                <option value="telefone">Telefone</option>
                <option value="aleatoria">Chave aleatória</option>
              </select>
            </div>
            <PField label="Chave Pix" testid="payout-pix-input" value={pForm.pix_key} onChange={(v) => setPForm({ ...pForm, pix_key: v })} placeholder="Sua chave Pix" />
            <button data-testid="payout-save-btn" onClick={requestSave} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-bold text-white transition-colors hover:bg-blue-700">
              <ShieldCheck className="h-4 w-4" />Salvar (confirmar com PIN)
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* PIN de segurança (ação crítica) */}
      <Dialog open={pinOpen} onOpenChange={setPinOpen}>
        <DialogContent data-testid="payout-pin-modal" className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-heading text-base font-bold text-slate-900"><ShieldCheck className="h-5 w-5 text-blue-600" />Confirmação de segurança</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Ação crítica: alterar a conta de recebimento. Digite seu PIN de 4 dígitos.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input data-testid="payout-pin-input" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" placeholder="••••" className="text-center font-mono text-2xl tracking-[0.6em]" />
            <button data-testid="payout-pin-confirm" onClick={confirmPin} disabled={pin.length !== 4 || savingPayout} className="flex w-full items-center justify-center gap-2 rounded-xl bg-slate-900 py-3 text-sm font-bold text-white transition-colors hover:bg-slate-800 disabled:opacity-50">
              {savingPayout ? <><Loader2 className="h-4 w-4 animate-spin" />Salvando...</> : "Confirmar e salvar"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PField = ({ label, testid, value, onChange, placeholder = "" }) => (
  <div>
    <Label className="text-xs font-semibold text-slate-600">{label}</Label>
    <Input data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="mt-1.5" />
  </div>
);

const ptoggle = (active) =>
  `flex items-center justify-center rounded-xl border px-3 py-2 text-sm font-semibold transition-colors ${active ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`;
